import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  CaseExportWithCaseInfo,
  ExportStatus,
  GetCaseExportDownloadUrlDocument,
  GetLensExportsDocument,
} from "generated/graphql";
import formatSizeBytes from "helpers/formatSizeBytes";
import { useContext, useEffect, useMemo, useState } from "react";
import theme, { LinkSX } from "theme";
import { P } from "theme/typography";
import CaseFilter from "./CaseFilter";
import { fullName, fullNameFromParts } from "helpers/name-helpers";
import { backend, displayDateAndTimeCompact, extractCaseIdAndName, INTERNAL_USER_NAME, isNumber } from "Backend";
import DateRangeFilter from "./DateRangeFilter";
import ClearFiltersButton from "./ClearFiltersButton";
import SimpleMenu from "components/SimpleMenu";
import { ActionItem } from "../case/intakes/IntakeRow";
import { Delete, Download, GetApp, Share } from "@mui/icons-material";
import { triggerCaseExportDownload } from "../case/CaseExports";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import DebouncedText from "components/DebouncedText";
import { runGqlQuery } from "gqlClient";
import SkeletonRow from "components/SkeletonRow";
import { add } from "date-fns";
import { triggerOpenFileWithUrl } from "helpers/triggerDownload";
import { useAppSelector } from "redux/hooks";
import { selectClientLens } from "redux/slices/clientLens";
import CheckAll from "./CheckAll";
import { selectUsers } from "redux/slices/client";
import UserAutoComplete from "components/UserAutocomplete";
import { useSnackbar } from "notistack";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import BulkDownloadDialog from "./BulkDownloadDialog";
import BulkShareDialog from "./BulkShareDialog";
import FirmAutocomplete from "components/FirmAutocomplete";
import { selectEnableFirmFeatureSet } from "redux/slices/session";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { useFirmsByLensId } from "helpers/hooks/useAllowedLenses";
import { ExportDisplayStatus } from "../case/CaseExportItem";
import { stripNull } from "helpers/coll";

const CollectURL = new URL("../case/collect_24.png", import.meta.url).href;

function comapreCaseExports(a: CaseExportWithCaseInfo, b: CaseExportWithCaseInfo): boolean {
  return a.caseExport.id === b.caseExport.id;
}

interface Props {
  caseExport: CaseExportWithCaseInfo;
  selected: boolean;
  onSelect: (caseExport: CaseExportWithCaseInfo) => void;
}

const LensExportRow = (props: Props) => {
  const { caseExport, selected, onSelect } = props;
  const createdBy = stripNull(caseExport.caseExport.createdBy);
  const isComplete = caseExport.caseExport.status === ExportStatus.Complete;
  const { searchParams, updateParam } = useSearchParamsWithUpdate();
  const [isDeleting, setIsDeleting] = useState(false);
  const { lensId } = useParamNumbers(["lensId"]);
  const firms = useFirmsByLensId(lensId);
  const hasFirmFeatureSet = useAppSelector(selectEnableFirmFeatureSet);
  const firmName = useMemo(() => {
    const firm = firms.find((f) => f.id === caseExport.firmId);
    return firm ? firm.name : "";
  }, [firms, caseExport.firmId]);

  const { enqueueSnackbar } = useSnackbar();
  const incrementCounter = () => {
    const lastInc = searchParams.get("inc");
    updateParam("inc", lastInc !== null ? `${parseInt(lastInc) + 1}` : "1");
  };
  const handleDeleteCaseExport = async () => {
    setIsDeleting(true);
    try {
      await backend.deleteCaseExport(caseExport.caseExport.id);
      enqueueSnackbar("Export deleted", { variant: "success" });
      incrementCounter();
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Failed to delete export", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };
  const actionItems: ActionItem[] = [
    {
      icon: <Download />,
      name: "Download",
      action: () => triggerCaseExportDownload(caseExport.caseExport.caseId, caseExport.caseExport.id),
    },
    {
      icon: <Delete />,
      name: "Delete",
      disabled: isDeleting,
      action: handleDeleteCaseExport,
    },
  ];

  const openExportInNewTab = async () => {
    await runGqlQuery(GetCaseExportDownloadUrlDocument, {
      caseId: caseExport.caseExport.caseId,
      caseExportId: caseExport.caseExport.id,
      inline: true,
    })
      .then((r) => triggerOpenFileWithUrl(r.case.exports.getExportDownloadUrl.url))
      .catch(() => enqueueSnackbar("Unable to open export", { variant: "error" }));
  };

  return (
    <TableRow>
      <TableCell sx={{ px: 0 }}>
        {isComplete ? (
          <Checkbox checked={selected} onChange={() => onSelect(caseExport)} size="small" color="secondary" />
        ) : (
          <></>
        )}
      </TableCell>
      <TableCell align="left">{isComplete ? <SimpleMenu hamburger items={actionItems} /> : <></>}</TableCell>
      <TableCell>{fullNameFromParts(caseExport.claimantGivenName, caseExport.claimantSurname)}</TableCell>
      <TableCell>{caseExport.clientCaseId}</TableCell>
      <TableCell sx={isComplete ? LinkSX : {}} onClick={isComplete ? openExportInNewTab : undefined}>
        {caseExport.caseExport.fileName}{" "}
        {caseExport.caseExport.collectShared && <img title="Shared with Collect" src={CollectURL} />}
      </TableCell>
      <TableCell>
        <ExportDisplayStatus status={caseExport.caseExport.status} />
      </TableCell>
      <TableCell>{displayDateAndTimeCompact(caseExport.caseExport.createdAt)}</TableCell>
      <TableCell>{caseExport.caseExport.exportType}</TableCell>
      <TableCell>
        {caseExport.caseExport.sizeBytes !== undefined && typeof caseExport.caseExport.sizeBytes === "number"
          ? formatSizeBytes(caseExport.caseExport.sizeBytes)
          : ""}
      </TableCell>
      <TableCell>{createdBy !== undefined ? fullName(createdBy) : INTERNAL_USER_NAME}</TableCell>
      {hasFirmFeatureSet && <TableCell>{firmName}</TableCell>}
    </TableRow>
  );
};

const LensExports = () => {
  const { searchParams, updateParam, updateParams } = useSearchParamsWithUpdate();
  const users = useAppSelector(selectUsers);
  const { lensId } = useParamNumbers(["lensId"]);
  const lens = useAppSelector(selectClientLens);
  const hasFirmFeatureSet = useAppSelector(selectEnableFirmFeatureSet);
  const exportType = searchParams.get("exportType");
  const [selectedExports, setSelectedExports] = useState<CaseExportWithCaseInfo[]>([]);
  const [exports, setExports] = useState<CaseExportWithCaseInfo[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const selectableExports = exports.filter((caseExport) => caseExport.caseExport.status === ExportStatus.Complete);
  const searchParamsPage = searchParams.get("page");
  const searchParamsRowsPerPage = searchParams.get("rowsPerPage");
  const pagination = {
    page: searchParamsPage !== null ? parseInt(searchParamsPage) : 1,
    rowsPerPage: searchParamsRowsPerPage !== null ? parseInt(searchParamsRowsPerPage) : 50,
  };
  const qpFirmId = searchParams.get("firmId");
  const firmId = qpFirmId !== null ? parseInt(qpFirmId) : undefined;
  const qpCreatedBy = searchParams.get("createdBy");
  const createdBy = qpCreatedBy !== null ? parseInt(qpCreatedBy) : undefined;
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkShareOpen, setBulkShareOpen] = useState(false);
  const confirmCtx = useContext(ConfirmationDialogContext);
  const [inc, setInc] = useState(0);
  const incrementCounter = () => {
    setInc((prev) => prev + 1);
  };
  const { enqueueSnackbar } = useSnackbar();

  const triggerBulkDelete = async () => {
    const exportIds = selectedExports.map((e) => e.caseExport.id);
    try {
      await Promise.all(exportIds.map((exportId) => backend.deleteCaseExport(exportId)));
      enqueueSnackbar(`${exportIds.length} ${exportIds.length === 1 ? "Export" : "Exports"} Deleted`, {
        variant: "success",
      });
      setSelectedExports([]);
      incrementCounter();
    } catch (e) {
      console.error(e);
      enqueueSnackbar(`Unable to delete ${exportIds.length === 1 ? "export" : "exports"}`, { variant: "error" });
    }
  };

  const deleteExports = () => {
    confirmCtx.confirm({
      title: `Delete ${selectedExports.length === 1 ? "Export" : "Exports"}`,
      prompt: (
        <P variant="sm">
          Are you sure you want to delete the selected {selectedExports.length === 1 ? "export" : "exports"}?
        </P>
      ),
      callback: async () => {
        await triggerBulkDelete();
      },
    });
  };

  useEffect(() => {
    if (lensId !== undefined) {
      setLoading(true);
      const cases = searchParams.getAll("cases");
      const exportType = searchParams.get("exportType");
      const qpPage = searchParams.get("page");
      const qpLimit = searchParams.get("rowsPerPage");
      const limit = qpLimit !== null ? parseInt(qpLimit) : 50;
      const page = qpPage !== null ? parseInt(qpPage) - 1 : 0;
      const createdDateStart = searchParams.get("createdDateStart");
      const createdDateEnd = searchParams.get("createdDateEnd");
      const minCreatedAt = createdDateStart !== null ? new Date(createdDateStart).toISOString() : undefined;
      const maxCreatedAt =
        createdDateEnd !== null ? add(new Date(createdDateEnd), { days: 1 }).toISOString() : undefined;
      runGqlQuery(GetLensExportsDocument, {
        lensId,
        caseIds: cases.map((c) => extractCaseIdAndName(c)[0]).filter(isNumber),
        firmId,
        minCreatedAt,
        maxCreatedAt,
        offset: page * limit,
        limit,
        createdBy,
        exportTypeQuery: exportType,
      })
        .then((r) => {
          setExports(r.lens.queryCaseExports.exports);
          setTotal(r.lens.queryCaseExports.total);
          setLoading(false);
        })
        .catch(() => {
          enqueueSnackbar("There was a problem loading the exports. Please try again later.", {
            variant: "error",
          });
        });
    }
  }, [searchParams, lensId, inc]);

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[100]}`,
          p: 2,
          borderRadius: `${theme.shape.borderRadius}px`,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: "auto",
        }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <P variant="subtitle1heavy">Filters</P>
          <ClearFiltersButton qpIgnore={["page", "rowsPerPage", "inc"]} />
        </Box>
        {hasFirmFeatureSet && (
          <FirmAutocomplete
            lensId={lensId}
            firmId={firmId}
            onChange={(fId) => updateParam("firmId", fId !== null ? `${fId}` : undefined)}
          />
        )}
        <CaseFilter />
        <DebouncedText
          value={exportType === null ? "" : exportType}
          label="Export Type"
          onChange={(n) => updateParam("exportType", n)}
        />
        <DateRangeFilter
          startKey="createdDateStart"
          startLabel="Created Date Start"
          endKey="createdDateEnd"
          endLabel="Created Date End"
        />
        <UserAutoComplete
          label="Created By"
          options={users}
          selected={users.find((u) => u.id === createdBy)}
          onChange={(user) => updateParam("createdBy", user ? `${user.id}` : undefined)}
        />
      </Box>

      <Box
        sx={{
          padding: 0.5,
          backgroundColor: "white",
          border: `1px solid ${theme.palette.grey[100]}`,
          borderTopLeftRadius: `${theme.shape.borderRadius}px`,
          borderTopRightRadius: `${theme.shape.borderRadius}px`,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "auto",
        }}>
        <Box sx={{ display: "flex", ml: 1, my: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: `1px solid ${theme.palette.grey[100]}`,
              borderRadius: "8px",
              px: 2,
            }}>
            <P>Bulk Actions</P>
            <Tooltip title="Download" arrow>
              <span>
                <IconButton disabled={!selectedExports.length} onClick={() => setBulkDialogOpen(true)}>
                  <GetApp />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <span>
                <IconButton disabled={!selectedExports.length} id="exports_table_delete" onClick={deleteExports}>
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Share with Collect">
              <span>
                <IconButton
                  disabled={!selectedExports.length}
                  id="exports_table_share_collect"
                  onClick={() => setBulkShareOpen(true)}>
                  <Share />
                </IconButton>
              </span>
            </Tooltip>
            {selectedExports.length > 0 && (
              <P variant="xs">
                {selectedExports.length} {selectedExports.length === 1 ? "Export" : "Exports"} Selected
              </P>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
        </Box>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  th: {
                    whiteSpace: "nowrap",
                  },
                }}>
                <TableCell sx={{ px: 0 }}>
                  <CheckAll<CaseExportWithCaseInfo>
                    selectedItems={selectedExports}
                    items={selectableExports}
                    setItems={setSelectedExports}
                    compareFn={comapreCaseExports}
                  />
                </TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Claimant</TableCell>
                <TableCell>Case Id</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Created By</TableCell>
                {hasFirmFeatureSet && <TableCell>Firm</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <>
                  {[0, 1, 2].map((i) => (
                    <SkeletonRow key={i} columnNum={10} />
                  ))}
                </>
              ) : (
                <>
                  {exports.map((e) => (
                    <LensExportRow
                      key={e.caseExport.id}
                      caseExport={e}
                      selected={!!selectedExports.find((se) => se.caseExport.id === e.caseExport.id)}
                      onSelect={(caseExport) => {
                        if (selectedExports.find((se) => se.caseExport.id === caseExport.caseExport.id)) {
                          setSelectedExports(
                            selectedExports.filter((fe) => fe.caseExport.id !== caseExport.caseExport.id)
                          );
                        } else {
                          setSelectedExports([...selectedExports, caseExport]);
                        }
                      }}
                    />
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Table>
          <TableBody>
            <TableRow>
              <TablePagination
                sx={{
                  ".MuiTablePagination-spacer": {
                    flex: 0,
                  },
                }}
                count={total}
                rowsPerPage={pagination.rowsPerPage}
                page={pagination.page - 1}
                onPageChange={(_evt, page) =>
                  updateParams([
                    {
                      key: "page",
                      value: `${page + 1}`,
                    },
                    {
                      key: "rowsPerPage",
                      value: `${pagination.rowsPerPage}`,
                    },
                  ])
                }
                onRowsPerPageChange={(evt) =>
                  updateParams([
                    {
                      key: "rowsPerPage",
                      value: evt.target.value,
                    },
                    { key: "page", value: `1` },
                  ])
                }
              />
            </TableRow>
          </TableBody>
        </Table>
      </Box>
      {bulkDialogOpen && (
        <BulkDownloadDialog
          lensName={lens.name}
          open={bulkDialogOpen}
          onClose={() => setBulkDialogOpen(false)}
          exports={selectedExports}
        />
      )}
      {bulkShareOpen && (
        <BulkShareDialog
          open={bulkShareOpen}
          onClose={() => {
            setBulkShareOpen(false);
            incrementCounter();
            setSelectedExports([]);
          }}
          exports={selectedExports}
        />
      )}
    </>
  );
};

export default LensExports;
